import { useSeo } from '../hooks/useSeo';
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Check, Euro, Calendar, Star, ArrowRight, Sparkles } from 'lucide-react';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ServicesPage = () => {
  useSeo("Services & tarifs", "Conseil, dessin technique, plans cotés et modélisation 3D : découvrez les prestations JENMA Concept.", '/services');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setPaymentDialogOpen(true);
  };

  const services = [
    {
      id: 'consultation',
      name: 'Consultation Initiale',
      price: 545,
      lookupKey: 'consultation_545',
      description: 'Consultation personnalisée pour votre projet d\'aménagement',
      features: [
        'Analyse de vos besoins',
        'Étude de faisabilité',
        'Conseils personnalisés',
        'Plan d\'action détaillé',
        'Devis sur mesure'
      ],
      popular: false
    },
    {
      id: 'residential',
      name: 'Forfait Résidentiel',
      price: 2500,
      lookupKey: 'forfait_residential_2500',
      description: 'Aménagement complet d\'espace résidentiel',
      features: [
        'Design d\'intérieur complet',
        'Plans 3D et visualisations',
        'Sélection de matériaux',
        'Coordination des travaux',
        'Suivi jusqu\'à finalisation',
        'Garantie satisfaction'
      ],
      popular: true
    },
    {
      id: 'commercial',
      name: 'Forfait Commercial',
      price: 5000,
      lookupKey: 'forfait_commercial_5000',
      description: 'Aménagement d\'espace commercial ou professionnel',
      features: [
        'Étude d\'image de marque',
        'Optimisation des espaces',
        'Design sur mesure',
        'Gestion de projet complète',
        'Livraison clé en main',
        'Support post-installation'
      ],
      popular: false
    },
    {
      id: 'suivi',
      name: 'Abonnement Suivi',
      price: 150,
      recurring: 'mois',
      lookupKey: 'suivi_monthly_150',
      description: 'Conseils et suivi personnalisé chaque mois',
      features: [
        '1h de consultation mensuelle',
        'Conseils personnalisés',
        'Veille tendances',
        'Ajustements déco',
        'Priorité sur les rendez-vous'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-dark py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-white mb-4">
            Nos <span className="text-gold">Services & Tarifs</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Des solutions d'agencement haut de gamme adaptées à vos besoins
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-neon" />
              Paiement sécurisé
            </span>
            <span className="mx-2">•</span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-neon" />
              Taxes incluses
            </span>
            <span className="mx-2">•</span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-neon" />
              Garantie satisfaction
            </span>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {services.map((service) => (
            <Card 
              key={service.id}
              className={`relative overflow-hidden transition-all hover:scale-105 ${
                service.popular 
                  ? 'bg-gradient-to-br from-gold/20 to-gold/5 border-gold' 
                  : 'bg-light/5 border-gold/20'
              }`}
            >
              {service.popular && (
                <div className="absolute top-4 right-4">
                  <div className="bg-neon text-dark px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    POPULAIRE
                  </div>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-white text-2xl font-playfair">
                  {service.name}
                </CardTitle>
                <CardDescription className="text-gray-400 min-h-[40px]">
                  {service.description}
                </CardDescription>
                
                <div className="mt-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gold">
                      {service.price}
                    </span>
                    <Euro className="w-6 h-6 text-gold" />
                    {service.recurring && (
                      <span className="text-gray-400 text-sm">/{service.recurring}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">TTC - Taxes incluses</p>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handleServiceClick(service)}
                  className={`w-full ${
                    service.popular
                      ? 'bg-gold hover:bg-gold-light text-dark'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-gold/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{service.recurring ? 'S\'abonner maintenant' : 'Réserver maintenant'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Custom Quote */}
          <Card className="bg-gradient-to-br from-white/5 to-white/10 border-gold/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8 text-gold" />
                <CardTitle className="text-white text-2xl font-playfair">
                  Devis Personnalisé
                </CardTitle>
              </div>
              <CardDescription className="text-gray-300">
                Votre projet est unique ? Parlons-en ensemble.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">
                Contactez-nous pour un devis sur mesure adapté à vos besoins spécifiques.
              </p>
              <Button
                onClick={() => window.location.href = '/#contact'}
                variant="outline"
                className="w-full border-gold/30 text-gold hover:bg-gold/10"
              >
                Demander un devis
              </Button>
            </CardContent>
          </Card>

          {/* Appointment Deposit */}
          <Card className="bg-gradient-to-br from-white/5 to-white/10 border-gold/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-8 h-8 text-gold" />
                <CardTitle className="text-white text-2xl font-playfair">
                  Acompte Rendez-vous
                </CardTitle>
              </div>
              <CardDescription className="text-gray-300">
                Réservez votre rendez-vous d'étude - 100€
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">
                Déduit du montant final du projet. Garantit votre créneau.
              </p>
              <Button
                onClick={() => handleServiceClick({
                  lookupKey: 'acompte_100',
                  name: 'Acompte Rendez-vous',
                  recurring: false
                })}
                className="w-full bg-gold hover:bg-gold-light text-dark"
              >
                Réserver avec acompte
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-8 text-sm text-gray-400">
            <span className="flex items-center gap-2">
              <Check className="w-5 h-5 text-neon" />
              Paiement sécurisé par Stripe
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-5 h-5 text-neon" />
              Remboursement sous 14 jours
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-5 h-5 text-neon" />
              Support client dédié
            </span>
          </div>
        </div>
      </div>

      {/* Payment Method Selector Dialog */}
      {selectedService && (
        <PaymentMethodSelector
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setSelectedService(null);
          }}
          lookupKey={selectedService.lookupKey}
          serviceName={selectedService.name}
          isSubscription={selectedService.recurring ? true : false}
        />
      )}
    </div>
  );
};